import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import Navigation from '@/components/navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Medal, Award } from 'lucide-react';

export default function Leaderboard() {
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      setTimeout(() => {
        window.location.href = '/api/login';
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  const { data: leaderboard, isLoading: leaderboardLoading } = useQuery<any[]>({
    queryKey: ['/api/leaderboard'],
    retry: false,
  });

  const { data: companyStats, isLoading: statsLoading } = useQuery<any>({
    queryKey: ['/api/company/stats'],
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hydro-100">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-lilac-300 border-t-purple-500"></div>
      </div>
    );
  }

  const getRankIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-lilac-500" />;
      case 3:
        return <Award className="w-5 h-5 text-orange-400" />;
      default:
        return null;
    }
  };

  const getRankStyle = (position: number, isCurrentUser: boolean) => {
    if (isCurrentUser) {
      return 'bg-purple-100 border border-purple-300';
    }

    switch (position) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-lilac-100 to-lilac-50 border border-lilac-300';
      case 3:
        return 'bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200';
      default:
        return 'bg-lilac-50 border border-lilac-200';
    }
  };

  return (
    <div className="min-h-screen pb-24 bg-hydro-100 font-kumbh">
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-deep-800 mb-2">Company Leaderboard</h1>
          <p className="text-deep-500">See how you rank among your colleagues</p>
        </div>

        {/* Company Overview */}
        <Card className="mb-6 p-5 bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200 border-none">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-deep-800">Company Overview</h3>
          </div>
          {statsLoading ? (
            <div className="grid grid-cols-3 gap-3 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="text-center p-3 bg-white/50 rounded-xl">
                  <div className="h-6 bg-lilac-200 rounded mb-2"></div>
                  <div className="h-3 bg-lilac-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : companyStats ? (
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div
                  className="text-xl font-bold text-purple-600"
                  data-testid="text-total-employees"
                >
                  {companyStats.totalEmployees}
                </div>
                <p className="text-xs text-deep-500">Employees</p>
              </div>
              <div className="text-center p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="text-xl font-bold text-purple-600" data-testid="text-active-today">
                  {companyStats.activeUsers}
                </div>
                <p className="text-xs text-deep-500">Active Today</p>
              </div>
              <div className="text-center p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-lilac-300">
                <div className="text-xl font-bold text-purple-600" data-testid="text-company-avg">
                  {companyStats.avgScore}%
                </div>
                <p className="text-xs text-deep-500">Avg Score</p>
              </div>
            </div>
          ) : (
            <div className="text-center text-deep-500">Unable to load company statistics</div>
          )}
        </Card>

        {/* Leaderboard */}
        <Card className="p-5 bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200 border-none">
          <h3 className="font-semibold text-deep-800 mb-4">Rankings</h3>
          {leaderboardLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-white/50 rounded-xl animate-pulse"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-lilac-200 rounded-full"></div>
                    <div>
                      <div className="h-4 bg-lilac-200 rounded mb-1 w-24"></div>
                      <div className="h-3 bg-lilac-200 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="h-4 bg-lilac-200 rounded w-12"></div>
                </div>
              ))}
            </div>
          ) : leaderboard && leaderboard.length > 0 ? (
            <div className="space-y-3">
              {leaderboard.map((member: any, index: number) => {
                const position = index + 1;
                const isCurrentUser = member.userId === user?.id;

                return (
                  <div
                    key={member.userId}
                    className={`flex items-center justify-between p-3 rounded-xl transition-all ${getRankStyle(position, isCurrentUser)}`}
                    data-testid={`row-leaderboard-${position}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            position === 1
                              ? 'bg-yellow-400 text-white'
                              : position === 2
                                ? 'bg-lilac-400 text-white'
                                : position === 3
                                  ? 'bg-orange-400 text-white'
                                  : 'bg-lilac-200 text-deep-600'
                          }`}
                        >
                          {position}
                        </div>
                        {getRankIcon(position)}
                      </div>

                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold text-xs">
                          {member.firstName?.charAt(0)}
                          {member.lastName?.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p
                              className="font-medium text-deep-800 text-sm"
                              data-testid={`text-name-${member.userId}`}
                            >
                              {member.firstName} {member.lastName}
                            </p>
                            {isCurrentUser && (
                              <Badge
                                variant="secondary"
                                className="bg-purple-500 text-white text-xs px-2 py-0"
                              >
                                You
                              </Badge>
                            )}
                          </div>
                          <p
                            className="text-xs text-deep-500"
                            data-testid={`text-department-${member.userId}`}
                          >
                            {member.department || 'Employee'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <p
                        className="font-semibold text-purple-600 text-sm"
                        data-testid={`text-points-${member.userId}`}
                      >
                        {Math.round(member.totalPoints || 0)} pts
                      </p>
                      <p className="text-xs text-deep-500">
                        {Math.round(member.avgScore || 0)}% avg
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-deep-500 py-8">
              <Trophy className="w-12 h-12 text-lilac-400 mx-auto mb-4" />
              <p className="text-base font-medium mb-2">No rankings available yet</p>
              <p className="text-sm">Start tracking your hydration to see the leaderboard!</p>
            </div>
          )}
        </Card>
      </div>

      <Navigation />
    </div>
  );
}
